/**
 * Support-facing walkthrough for Framework V2.
 * Route: /support-guide
 * Audience: the person on the phone with a member. Not marketing copy.
 */

export const SUPPORT_PAGE = {
    eyebrow: 'Support guide',
    title: 'Oola Framework',
    subtitle:
        'How to walk a member through the current portal. Use this beside the member’s screen. It describes Framework V2, the experience at dashboard.oolalife.com.',
    notes: [
        'Members sign in at https://dashboard.oolalife.com/login. The classic screens still exist for staff, but do not send a member there.',
        'The marketing preview at /marketing-assets shows the older interface and campaign copy. It is not this guide.',
    ],
};

export const SUPPORT_HOW_TO = {
    title: 'How to use this guide',
    items: [
        'Keep this page open. Ask the member to open dashboard.oolalife.com/login on their phone or computer.',
        'Match what they see to a section below, then read the “Walk them through” steps.',
        'Use “If they say…” before escalating. Most streak and “missing program” reports are a sign-in, a time zone, or a purchase on a different Customer Id.',
    ],
};

export const SUPPORT_NAV = [
    { id: 'sign-in', label: 'Sign in' },
    { id: 'first-visit', label: 'Design My Life' },
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'streak', label: 'Actions and streak' },
    { id: 'goals', label: 'Goals' },
    { id: 'elearning', label: 'E-Learning' },
    { id: 'programs', label: 'Additional Programs' },
    { id: 'profile', label: 'Profile and time zone' },
    { id: 'trouble', label: 'When it still fails' },
];

export const SUPPORT_SECTIONS = [
    {
        id: 'sign-in',
        phase: 'Start here',
        title: 'Sign in',
        summary:
            'The login card has two tabs. Customer Id is the one that matches a USANA account. Phone works only with the 10-digit number on that same account.',
        preview: 'login',
        walk: [
            'Open https://dashboard.oolalife.com/login.',
            'Choose Customer Id. Enter the USANA Customer Id, then the password, then Sign in.',
            'Phone is the other tab. It must be 10 digits, with no email address.',
            'After a successful sign-in the portal opens where they left off: time zone, Design My Life, the dashboard, or the membership screen.',
        ],
        cases: [
            {
                issue: '“It says login failed.”',
                do: 'Confirm they are on Customer Id, not Phone, and that the id is the USANA customer number. Email is not a login on this form.',
            },
            {
                issue: '“I forgot my password.”',
                do: 'Reset password on the login card opens USANA’s reset page. Oola does not set that password.',
            },
            {
                issue: '“I only have an email.”',
                do: 'Look up their Customer Id in USANA and have them use that tab. Do not ask them to invent a password reset on the phone tab.',
            },
            {
                issue: 'The page says Unlock your full Oola experience.',
                do: 'The account signed in, but there is no Framework subscription on it. They can open a limited dashboard. Action Tracker, the blueprint, and e-learning need the subscription. Purchased Additional Programs can still be opened from the menu.',
            },
            {
                issue: 'A link from Oola opens a blank page.',
                do: 'Ask for the exact link and send it to the Oola product team. Do not keep asking the member to refresh.',
            },
        ],
    },
    {
        id: 'first-visit',
        phase: 'First sessions',
        title: 'Design My Life Blueprint',
        summary:
            'New members finish three steps before the daily dashboard is their home. Breakthrough stays locked until this blueprint is done.',
        preview: 'steps',
        walk: [
            'If the portal asks for a time zone, set the zone where they live. That zone decides which calendar day an action belongs to.',
            'Step 1 is the life assessment across the 7 areas. They answer, then see the Oola Wheel.',
            'Step 2 is goals: choose goals, narrow them to a Top 7, and pick one Oola One for the year.',
            'Step 3 is daily actions: the small habits they will check off. Saving these finishes the blueprint and opens the dashboard.',
        ],
        cases: [
            {
                issue: '“I finished, but it keeps sending me back to the questions.”',
                do: 'Have them finish the step on screen and use the save or continue button on that step. Closing the browser in the middle leaves them on that step.',
            },
            {
                issue: '“E-Learning says I have to complete Design My Life.”',
                do: 'That message is expected. Walk them through Steps 1–3. The lesson library opens after the blueprint is saved.',
            },
        ],
    },
    {
        id: 'dashboard',
        phase: 'Every day',
        title: 'Dashboard',
        summary:
            'The left menu is the whole product. On a phone the same items show as Home, Tracker, Goals, Learn, More, and Profile.',
        preview: 'nav',
        walk: [
            'Dashboard is the home view: today’s actions, goals, and the learning strip.',
            'Action Tracker is where they mark habits done.',
            'Goals shows the Top 7 and the Oola One from the blueprint.',
            'E-Learning is My Breakthrough and, later, My Path.',
            'Additional Programs is only for courses they bought separately.',
            'Profile is the name, photo, and time zone.',
        ],
        cases: [
            {
                issue: '“I don’t see Goals or E-Learning.”',
                do: 'They are likely on the limited dashboard without a Framework subscription. Check the Customer Id against the Framework order.',
            },
            {
                issue: '“This does not look like the screenshots I was sent.”',
                do: 'Those screenshots are often the classic portal or the marketing preview. Stay on dashboard.oolalife.com. The menu names above are the current ones.',
            },
        ],
    },
    {
        id: 'streak',
        phase: 'Every day',
        title: 'Actions and the streak',
        summary:
            'A day counts toward the streak only when at least 3 actions are completed on that calendar day in the member’s time zone. The current streak drops when a day is missed. The longest streak does not.',
        preview: 'streak',
        walk: [
            'Open Action Tracker. The tabs are Tracker, Daily Actions, and To-Do List.',
            'On Daily Actions, have them mark today’s habits complete. The ring fills at 3.',
            'They can log up to 15 actions in a day. Extra actions past 3 still count as the same streak day.',
            'To-Do List can be locked without an active subscription. Tracker and Daily Actions are the streak.',
            'If they miss a day, the current streak shows 0 the next day. Completing 3 today starts a new current streak. The longest number stays.',
        ],
        cases: [
            {
                issue: '“I did my actions and the streak is still broken.”',
                do: 'Ask how many they completed on the missing day, and confirm Profile shows their real time zone. Actions late at night count on that local day, not on UTC. A day with only 1 or 2 actions does not count.',
            },
            {
                issue: '“I was traveling.”',
                do: 'The streak uses the time zone saved on the profile, not the phone’s current location. If the profile zone is wrong, later days will look off. Fix the zone before they log more actions.',
            },
            {
                issue: 'A day truly has fewer than 3 and the streak should be restored.',
                do: 'Oola staff can backfill that day from the admin Lab. USANA support escalates the Customer Id, the date, and the time zone. Do not tell the member the streak is already fixed until Oola confirms the day was written.',
            },
        ],
    },
    {
        id: 'goals',
        phase: 'Every day',
        title: 'Goals',
        summary:
            'Goals on the dashboard are the Top 7 and the Oola One they chose in Design My Life. They are not a second list to recreate.',
        preview: 'goals',
        walk: [
            'Open Goals from the left menu.',
            'Show them the Top 7 and which one is the Oola One.',
            'Daily habits that support those goals are marked in Action Tracker, not on the goal card.',
        ],
        cases: [
            {
                issue: '“My goals disappeared.”',
                do: 'Confirm they are signed in with the same Customer Id that finished the blueprint. A second account, or the limited non-subscriber view, will not show that blueprint.',
            },
        ],
    },
    {
        id: 'elearning',
        phase: 'After the blueprint',
        title: 'E-Learning',
        summary:
            'My Breakthrough is 12 weeks. A new module unlocks every 7 days. Each module is Watch, Read, then Do. My Path starts after Breakthrough, with a new module about every 30 days.',
        preview: 'learn',
        walk: [
            'Open E-Learning. Start on My Breakthrough if the blueprint is already saved.',
            'Open the unlocked module. Have them watch the lesson, read the page, and complete the exercise.',
            'The next module stays locked until its unlock date. That is the schedule, not a broken account.',
            'When Breakthrough is finished, My Path appears in the same E-Learning area.',
        ],
        cases: [
            {
                issue: '“The next lesson is locked.”',
                do: 'Check the unlock date on the module. If today’s date is before that date, ask them to wait. Do not reset the account.',
            },
            {
                issue: '“I can’t start Breakthrough.”',
                do: 'Read the message on the screen. If it says to complete Design My Life Blueprint, go back to the first-visit section.',
            },
        ],
    },
    {
        id: 'programs',
        phase: 'Separate purchase',
        title: 'Additional Programs',
        summary:
            'Courses such as add-on programs are not included with the Framework. They show up only when that purchase is tied to the Customer Id they used to sign in.',
        preview: 'programs',
        walk: [
            'Open Additional Programs in the left menu.',
            'If the course is listed, open it from there. It does not appear inside the 12-week Breakthrough board.',
            'If the list is empty, compare the order’s Customer Id with the id on the login screen.',
        ],
        cases: [
            {
                issue: '“I paid and it is not there.”',
                do: 'Confirm the order in USANA and the Customer Id on that order. Signing in with a spouse’s id, a phone on another account, or an old test account hides the purchase. After the ids match, have them sign out and back in.',
            },
        ],
    },
    {
        id: 'profile',
        phase: 'Settings',
        title: 'Profile and time zone',
        summary:
            'The time zone on Profile is what the streak and “today” use. The phone clock does not override it.',
        preview: 'profile',
        walk: [
            'Open Profile from the menu.',
            'Check the time zone with the member. Set it to where they normally live.',
            'Save before they mark more actions.',
        ],
        cases: [
            {
                issue: '“Today’s actions showed up on yesterday.”',
                do: 'The profile zone and the clock they used do not match. Correct the zone, then look at Action Tracker again. Do not ask them to delete actions.',
            },
        ],
    },
    {
        id: 'trouble',
        phase: 'Escalate',
        title: 'When it still fails',
        summary:
            'Use this when the steps above do not match the screen. Send facts, not a screenshot of the classic portal.',
        preview: null,
        walk: [
            'Customer Id they typed on the Customer Id tab.',
            'The menu item they are on, and the exact sentence on the screen.',
            'Whether they see the membership card “Unlock your full Oola experience”.',
            'For a streak: the profile time zone, the date they believe is missing, and how many actions that date shows.',
            'For a blank sign-in link: the full URL.',
        ],
        cases: [
            {
                issue: 'Who fixes it',
                do: 'USANA confirms the customer, the password, the subscription, and which Customer Id owns the order. Oola fixes a blank sign-in link, a blueprint that will not save, or a streak day that needs to be backfilled.',
            },
        ],
    },
];
